import { CalendarIcon, Clock } from '../../../../../../assets/icons';
import StatusBadge, { StatusBadgeVariant } from '../../../../../../common/components/xepp/status-badge/StatusBadge';
import { TransactionPaymentTypeMap, TransactionStatusMap } from '../../../../../../model/transaction';
import BlackCloseButton from '../../../../../../common/components/xepp/black-close-button/BlackCloseButton';
import { ServiceRequestInstanceDetailsResponse, SlaStatus, StepInstanceHistoryData } from '../../../../../../model/service-request/service-request-instance-details';
import { formatDate, formatTimeLocal, formatTimestamp } from '../../../../../../utils';
import styles from './ServiceRequestInstanceDetailsPanel.module.scss';

interface Props {
  data: ServiceRequestInstanceDetailsResponse;
  onClose: () => void;
}

const transactionStatusVariant: Record<number, StatusBadgeVariant> = {
  0: 'unknown',
  1: 'atRisk',
  2: 'completed',
  3: 'breached',
  4: 'refunded',
  5: 'unknown',
};

const slaStatusVariant: Record<SlaStatus, StatusBadgeVariant> = {
  [SlaStatus.OnTrack]: 'onTrack',
  [SlaStatus.AtRisk]: 'atRisk',
  [SlaStatus.Breached]: 'breached',
  [SlaStatus.Completed]: 'completed',
};


const slaLabel: Record<SlaStatus, string> = {
  [SlaStatus.OnTrack]: 'On Track',
  [SlaStatus.AtRisk]: 'At Risk',
  [SlaStatus.Breached]: 'Breached',
  [SlaStatus.Completed]: 'Completed',
};


function StepCard({ step }: { step: StepInstanceHistoryData }) {
  return (
    <div className={styles.stepCard}>
      <div className={styles.row}>
        <span className={styles.label}>{step.stepName}</span>
        <span className={styles.value}>{[step.assignedUserName?.firstName, step.assignedUserName?.lastName].filter(Boolean).join(' ') || 'Step not assigned'}</span>
      </div>
      <div className={styles.row}>
        <span className={styles.label}>Start Time</span>
        <span className={styles.value}>{formatTimestamp(step.createdAt)}</span>
      </div>
      <div className={styles.row}>
        <span className={styles.label}>End Time</span>
        <span className={styles.value}>{new Date(step.updatedAt).getFullYear() < 100 ? 'N/A' : formatTimestamp(step.updatedAt)}</span>
      </div>
    </div>
  );
}

const ServiceRequestInstanceDetailsPanel = ({ data, onClose }: Props) => {
  const customerName = [data.customerData?.firstName, data.customerData?.lastName]
    .filter(Boolean)
    .join(' ') || 'N/A';

  const createdBy = [data.submitterData?.firstName, data.submitterData?.lastName]
    .filter(Boolean)
    .join(' ') || 'N/A';

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.title}>{data.ticketNumberFormatted}</span>
          <div className={styles.dateTime}>
            <div className={styles.dateTimeItem}>
              <CalendarIcon width={18} height={18} />
              <span>{formatDate(data.createdAt)}</span>
            </div>
            <div className={styles.dateTimeItem}>
              <Clock width={18} height={18} />
              <span>{formatTimeLocal(data.createdAt)}</span>
            </div>
          </div>
        </div>
        <BlackCloseButton onClick={onClose} />
      </div>

      <div className={styles.body}>
        {/* Section 1 — Customer Details */}
        <div className={styles.section}>
          <span className={styles.sectionTitle}>Customer Details</span>
          <div className={styles.row}>
            <span className={styles.label}>Customer Name</span>
            <span className={styles.value}>{customerName}</span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Email</span>
            <span className={styles.value}>{data.customerData?.email ?? 'N/A'}</span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Phone</span>
            <span className={styles.value}>{data.customerData?.phone ?? 'N/A'}</span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Meter Number</span>
            <span className={styles.value}>{data.customerData?.meterNumber ?? 'N/A'}</span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Address</span>
            <span className={styles.value}>{data.customerData?.address ?? 'N/A'}</span>
          </div>
        </div>

        {/* Section 2 — Request Details */}
        <div className={styles.section}>
          <span className={styles.sectionTitle}>Request Details</span>
          <div className={styles.row}>
            <span className={styles.label}>Request Type</span>
            <span className={styles.value}>{data.requestType?.name ?? 'N/A'}</span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Created By</span>
            <span className={styles.value}>{createdBy}</span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Submission Source</span>
            <span className={styles.value}>{data.agentId ? 'Support Agent' : 'Customer App'}</span>
          </div>
        </div>

        {/* Section 3 — Payment Details */}
        {data.transaction && (
          <div className={styles.section}>
            <span className={styles.sectionTitle}>Payment Details</span>
            <div className={styles.row}>
              <span className={styles.label}>Status</span>
              <StatusBadge variant={transactionStatusVariant[data.transaction.status] ?? 'unknown'}>
                {TransactionStatusMap[data.transaction.status] ?? TransactionStatusMap[0]}
              </StatusBadge>
            </div>
            <div className={styles.row}>
              <span className={styles.label}>Date & Time</span>
              <span className={styles.value}>{formatTimestamp(data.transaction.updatedAt)}</span>
            </div>
            <div className={styles.row}>
              <span className={styles.label}>Channel</span>
              <span className={styles.value}>{TransactionPaymentTypeMap[data.transaction.transactionPaymentType ?? 0] ?? TransactionPaymentTypeMap[0]}</span>
            </div>
          </div>
        )}

        {/* Section 4 — Workflow Steps */}
        {data.historyStepsData && data.historyStepsData.length > 0 && (
          <div className={styles.section}>
            <span className={styles.sectionTitle}>Workflow Details</span>
            {data.historyStepsData.map(step => (
              <StepCard key={step.id} step={step} />
            ))}
          </div>
        )}

        {/* Section 5 — Current Assignee */}
        <div className={styles.section}>
          <span className={styles.sectionTitle}>Current Assignee</span>
          <div className={styles.row}>
            <span className={styles.label}>Name</span>
            <span className={styles.value}>{[data.currentStepData?.assignedUserName?.firstName, data.currentStepData?.assignedUserName?.lastName].filter(Boolean).join(' ') || 'Step not assigned'}</span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Department</span>
            <span className={styles.value}>{data.currentStepData?.departmentName ?? 'N/A'}</span>
          </div>
        </div>

        {/* Section 6 — Remaining Steps & SLA */}
        <div className={styles.section}>
          <span className={styles.sectionTitle}>Current Step & SLA</span>
          <div className={styles.row}>
            <span className={styles.label}>Step</span>
            <span className={styles.value}>{data.currentStepData?.stepName ?? 'N/A'}</span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>SLA</span>
            {data.currentStepData?.slaStatus !== undefined
              ? <StatusBadge variant={slaStatusVariant[data.currentStepData.slaStatus]}>{slaLabel[data.currentStepData.slaStatus]}</StatusBadge>
              : <span className={styles.value}>N/A</span>
            }
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceRequestInstanceDetailsPanel;
